import { Fragment, memo } from 'react';

import { KeyShortcut } from '~/components/KeyShortcut';
import { ShortcutInfo } from '~/components/ShortcutInfo';
import { type IconType } from '~/types/icons';
import { formatShortcutKey } from '~/utils/KeyUtils';

export type ShortcutSectionHeader = {
  id: string;
  type: 'header';
  label: string;
};

export type ShortcutItem = {
  id: string;
  type?: 'shortcut';
  icon: IconType;
  label: string;
  keys: string[];
  separator?: string;
};

export type ShortcutEntry = ShortcutSectionHeader | ShortcutItem;

export type Shortcut = {
  id: string;
  icon?: IconType;
  label?: string;
  keys: string[];
  separator?: string;
};

export type ShortcutsListProps = {
  shortcuts: (Shortcut | ShortcutEntry)[];
};

export const ShortcutsList = memo(({ shortcuts }: ShortcutsListProps) => {
  return (
    <>
      {shortcuts.map((entry) => {
        if ('type' in entry && entry.type === 'header') {
          return (
            <div key={entry.id} className="mt-2 pt-2 font-semibold ads:text-primary text-12 text-gray-12 first:mt-0">
              {entry.label}
            </div>
          );
        }

        return (
          <KeyShortcut key={entry.id} icon={entry.icon} text={entry.label}>
            {entry.keys.map((key, index, array) => {
              const isCombination = key.includes('+') && key.split('+').length > 1;
              const isLast = array[array.length - 1] === key;

              if (isCombination) {
                return (
                  <Fragment key={key}>
                    {key.split('+').map((keyPart) => {
                      return <ShortcutInfo key={keyPart}>{formatShortcutKey(keyPart)}</ShortcutInfo>;
                    })}
                  </Fragment>
                );
              }

              return (
                <Fragment key={`${key}-${index}`}>
                  <ShortcutInfo>{formatShortcutKey(key)}</ShortcutInfo>

                  {!isLast && (
                    <span className="mx-0.5 ads:text-metadata text-12 text-pigeon-400">{entry.separator || 'or'}</span>
                  )}
                </Fragment>
              );
            })}
          </KeyShortcut>
        );
      })}
    </>
  );
});

ShortcutsList.displayName = 'ShortcutsList';
